import React, { useMemo, useRef, useState } from "react";
import {
  Button,
  Card,
  Col,
  DatePicker,
  Divider,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Row,
  Select,
  Space,
  Typography,
} from "antd";
import dayjs, { Dayjs } from "dayjs";
import SelectAjax from "../../ui/SelectAjax";
import { formatCurrency } from "../../../Helpers/formatters";
import billingService from "../../../services/billing.service";
import PdfModal, { PdfModalRef } from "./PdfModal";
import CustomerForm from "../../Admin/Customers/CustomerForm";

const { Text, Title } = Typography;

const { Option } = Select;

type InvoiceItem = {
  name?: string;
  price_inc_igv?: number; // precio unitario con IGV
  qty?: number;
};

type InvoiceFormValues = {
  customer_id?: number | string;
  invoice_type?: "01" | "03";
  date?: Dayjs;
  payment_method?: string;
  due_date?: Dayjs;
  notes?: string;
  voucher?: string;
  items?: InvoiceItem[];
};

type InvoiceCreateProps = {
  open: boolean;
  onClose: () => void;
  onEmitted?: (payload: any) => void; // callback opcional
  igvRate?: number; // por defecto 0.18
};

const IGV_DEFAULT = 0.18;

const currency = "PEN";

function toNumber(n: any): number {
  const v = typeof n === "string" ? parseFloat(n) : n;
  return Number.isFinite(v) ? Number(v) : 0;
}

const InvoiceCreate: React.FC<InvoiceCreateProps> = ({
  open,
  onClose,
  onEmitted,
  igvRate = IGV_DEFAULT,
}) => {
  const pdfRef = useRef<PdfModalRef>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [customerIsVisible, setCustomerIsVisible] = useState(false);
  const [customerUrl, setCustomerUrl] = useState("customers-list");

  const onClickAddCustomer = () => {
    setCustomerIsVisible(true);
  };
  const showPdf = (url: string) => {
    pdfRef.current?.showPdf(url);
  };

  const [form] = Form.useForm<InvoiceFormValues>();

  // Totales reactivos
  const items: InvoiceItem[] = Form.useWatch("items", form) || [];

  const totals = useMemo(() => {
    const totalInc = (items || []).reduce((acc, it) => {
      const p = toNumber(it.price_inc_igv);
      const q = toNumber(it.qty);
      return acc + p * q;
    }, 0);

    const base = totalInc / (1 + igvRate);
    const igv = totalInc - base;

    return {
      base,
      igv,
      total: totalInc,
    };
  }, [items, igvRate]);

  const getPayload = async () => {
    const raw = await form.validateFields();
    const payload = {
      customer_id: raw.customer_id,
      invoice_type: raw.invoice_type, // 01 factura, 03 boleta
      date: raw.date ? dayjs(raw.date).format("YYYY-MM-DD") : null,
      due_date: raw.due_date ? dayjs(raw.due_date).format("YYYY-MM-DD") : null,
      notes: raw.notes || "",
      currency,
      igv_rate: igvRate,
      payment_method: raw.payment_method,
      voucher: raw.voucher,
      items: (raw.items || [])
        .filter((it) => !!it.name && toNumber(it.qty) > 0)
        .map((it) => {
          const price_inc_igv = toNumber(it.price_inc_igv);
          const qty = toNumber(it.qty);
          const line_total_inc = price_inc_igv * qty;
          const line_base = line_total_inc / (1 + igvRate);
          const line_igv = line_total_inc - line_base;
          return {
            name: it.name,
            price_inc_igv,
            qty,
            line_base: +line_base.toFixed(2),
            line_igv: +line_igv.toFixed(2),
            line_total_inc: +line_total_inc.toFixed(2),
          };
        }),
      totals: {
        base: +totals.base.toFixed(2),
        igv: +totals.igv.toFixed(2),
        total: +totals.total.toFixed(2),
      },
    };

    return payload;
  };

  const makeInvoice = async () => {
    setIsLoading(true);
    try {
      const payload = await getPayload();
      const data = await billingService.makeInvoice(payload);
      onEmitted?.(data);
      onClose();
      form.resetFields();
    } catch (error) {
      console.error(error);
      message.error(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEmit = async () => {
    setIsLoading(true);
    try {
      const payload = await getPayload();
      const response = await billingService.getPreview(payload);

      const buffer = response;
      const text = new TextDecoder().decode(buffer);

      // Intentar parsear como JSON
      try {
        const json = JSON.parse(text);
        message.error(json.message || "Ocurrió un error al generar el PDF");
        return;
      } catch {
        // No es JSON, por lo tanto es PDF
      }

      const blob = new Blob([buffer], { type: "application/pdf" });
      const fileURL = URL.createObjectURL(blob);
      showPdf(fileURL);
    } catch (error: any) {
      console.error(error);
      message.error(error.message || "Error desconocido");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>

    <CustomerForm
        title={"Registrar nuevo cliente"}
        method={"POST"}
        onSuccess={(customer) => {
          setCustomerUrl((url) => url + "?nc=" + Math.random() * 100);
          form.setFieldsValue({ customer_id: customer.id });
          setCustomerIsVisible(false);
        } }
        onCancel={() => {
          setCustomerIsVisible(false);
        } }
        visible={customerIsVisible} edit={undefined}              />

      <PdfModal
        isLoading={isLoading}
        onOk={makeInvoice}
        ref={pdfRef}
        title="Vista previa del PDF"
      />
      <Modal
        loading={isLoading}
        title={
          <div className="flex items-center gap-2">
            <span className="text-base font-semibold">Nueva Factura</span>
          </div>
        }
        open={open}
        onCancel={onClose}
        width={980}
        maskClosable={false}
        footer={
          <Space style={{ width: "100%", justifyContent: "space-between" }}>
            <Text type="secondary">
              IGV {Math.round(igvRate * 100)}% • {currency}
            </Text>
            <Space>
              <Button onClick={onClose}>Cancelar</Button>
              <Button type="primary" onClick={handleEmit}>
                Vista Previa y Emitir
              </Button>
            </Space>
          </Space>
        }
      >
        <Form
          form={form}
          size="middle"
          name="invoice-form"
          layout="horizontal"
          initialValues={{
            invoice_type: "01",
            date: dayjs(),
            items: [{ name: "", price_inc_igv: undefined, qty: 1 }],
          }}
          labelCol={{ span: 7 }}
          wrapperCol={{ span: 17 }}
        >
          <Card
            size="small"
            style={{ marginBottom: 12 }}
            bodyStyle={{ paddingBottom: 4 }}
          >
            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item
                  label="Cliente"
                  name="customer_id"
                  rules={[{ required: true, message: "Selecciona un cliente" }]}
                >
                  <SelectAjax
                    onNew={onClickAddCustomer}
                    showSearch
                    valueProp="id"
                    optionFilterProp="children"
                    placeholder="Buscar/seleccionar cliente"
                    url={customerUrl}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  label="Tipo de Comprobante"
                  name="invoice_type"
                  rules={[{ required: true }]}
                >
                  <Select
                    options={[
                      { value: "01", label: "01 - Factura" },
                      { value: "03", label: "03 - Boleta" },
                    ]}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} md={12}>
                <Form.Item
                  label="Fecha de emisión"
                  name="date"
                  rules={[
                    {
                      required: true,
                      message: "La fecha de emisión es obligatoria",
                    },
                  ]}
                >
                  <DatePicker
                    style={{ width: "100%" }}
                    format="YYYY-MM-DD"
                    disabledDate={(current) => {
                      if (!current) return false;
                      const now = dayjs();
                      return (
                        current.isBefore(now.subtract(2, "day"), "day") ||
                        current.isAfter(now, "day")
                      );
                    }}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} md={12}>
                <Form.Item
                  label="F. de vencimiento"
                  name="due_date"
                  rules={[{ required: false, message: "Validar fecha" }]}
                >
                  <DatePicker
                    style={{ width: "100%" }}
                    format="YYYY-MM-DD"
                    disabledDate={(current) => {
                      if (!current) return false;
                      const now = dayjs();
                      return (
                        current.isBefore(now.add(1, "day"), "day") ||
                        current.isAfter(now.add(6, "month"), "day")
                      );
                    }}
                    presets={[
                      { label: "7 días", value: dayjs().add(7, "day") },
                      { label: "15 días", value: dayjs().add(15, "day") },
                      { label: "30 días", value: dayjs().add(30, "day") },
                    ]}
                    placeholder="Selecciona la fecha o usa un atajo"
                  />
                </Form.Item>
              </Col>
            </Row>
          </Card>

          <Card
            size="small"
            title={<span style={{ fontWeight: 600 }}>Ítems</span>}
            extra={
              <Form.List name="items">
                {(fields, { add, remove }) => (
                  <Space>
                    <Button
                      onClick={() =>
                        add({ name: "", price_inc_igv: undefined, qty: 1 })
                      }
                    >
                      Agregar ítem
                    </Button>
                  </Space>
                )}
              </Form.List>
            }
          >
            <Form.List name="items">
              {(fields, { add, remove }) => (
                <div style={{ display: "grid", gap: 8 }}>
                  {/* Header */}
                  <Row gutter={8} style={{ fontWeight: 600, color: "#555" }}>
                    <Col xs={24} md={10}>
                      <Text>Nombre del ítem</Text>
                    </Col>
                    <Col xs={8} md={4}>
                      <Text>Precio c/IGV</Text>
                    </Col>
                    <Col xs={8} md={4}>
                      <Text>Cantidad</Text>
                    </Col>
                    <Col xs={8} md={4} style={{ textAlign: "right" }}>
                      <Text>Subtotal</Text>
                    </Col>
                    <Col xs={24} md={2} />
                  </Row>

                  <Divider style={{ margin: "8px 0" }} />

                  {fields.map((field, idx) => (
                    <Row key={field.key} gutter={8} align="middle">
                      <Col xs={24} md={10}>
                        <Form.Item
                          name={[field.name, "name"]}
                          rules={[
                            { required: true, message: "Nombre del ítem" },
                          ]}
                          style={{ marginBottom: 8 }}
                        >
                          <Input placeholder="Ej. Servicio de consultoría" />
                        </Form.Item>
                      </Col>

                      <Col xs={8} md={4}>
                        <Form.Item
                          name={[field.name, "price_inc_igv"]}
                          rules={[
                            { required: true, message: "Precio con IGV" },
                          ]}
                          style={{ marginBottom: 8 }}
                        >
                          <InputNumber
                            min={0}
                            precision={2}
                            placeholder="0.00"
                            style={{ width: "100%" }}
                          />
                        </Form.Item>
                      </Col>

                      <Col xs={8} md={4}>
                        <Form.Item
                          name={[field.name, "qty"]}
                          rules={[{ required: true, message: "Cantidad" }]}
                          style={{ marginBottom: 8 }}
                        >
                          <InputNumber
                            min={0}
                            precision={2}
                            placeholder="1"
                            style={{ width: "100%" }}
                          />
                        </Form.Item>
                      </Col>

                      <Col xs={8} md={4} style={{ textAlign: "right" }}>
                        <Form.Item
                          shouldUpdate={(prev, cur) => {
                            const pPrev = prev.items?.[idx]?.price_inc_igv;
                            const qPrev = prev.items?.[idx]?.qty;
                            const pCur = cur.items?.[idx]?.price_inc_igv;
                            const qCur = cur.items?.[idx]?.qty;
                            return pPrev !== pCur || qPrev !== qCur;
                          }}
                          style={{ marginBottom: 8 }}
                        >
                          {() => {
                            const it = form.getFieldValue([
                              "items",
                              idx,
                            ]) as InvoiceItem;
                            const subtotal =
                              toNumber(it?.price_inc_igv) * toNumber(it?.qty);
                            return <Text>{formatCurrency(subtotal)}</Text>;
                          }}
                        </Form.Item>
                      </Col>

                      <Col xs={24} md={2} style={{ textAlign: "right" }}>
                        <Button danger onClick={() => remove(field.name)}>
                          Eliminar
                        </Button>
                      </Col>
                    </Row>
                  ))}
                </div>
              )}
            </Form.List>
          </Card>

          <Row gutter={16} style={{ marginTop: 12 }}>
            <Col xs={24} md={12}>
              <Card size="small" title="Extra">
                <Form.Item
                  name="payment_method"
                  label="Método de pago"
                  initialValue={"cash"}
                  rules={[
                    { required: true, message: "Ingresa el método de pago" },
                  ]}
                >
                  <Select>
                    <Option value="cash">Efectivo</Option>
                    <Option value="yape">Yape</Option>
                    <Option value="bank-transfer">
                      Transferencia Bancaria
                    </Option>
                    <Option value="credit-card">Izipay</Option>
                  </Select>
                </Form.Item>

                <Form.Item
                  name="voucher"
                  label="N° Operación"
                  rules={[{ required: false, message: "Ingresa OC" }]}
                >
                  <Input />
                </Form.Item>

                <Form.Item
                  name="notes"
                  label="Observaciones"
                  style={{ marginBottom: 0 }}
                >
                  <Input.TextArea
                    placeholder="Notas u observaciones para la factura"
                    autoSize={{ minRows: 3, maxRows: 5 }}
                  />
                </Form.Item>
              </Card>
            </Col>

            <Col xs={24} md={12}>
              <Card
                size="small"
                title={<span style={{ fontWeight: 600 }}>Totales</span>}
                bodyStyle={{ paddingTop: 12 }}
              >
                <Row>
                  <Col span={12}>
                    <Text>Base imponible (sin IGV):</Text>
                  </Col>
                  <Col span={12} style={{ textAlign: "right" }}>
                    <Text>{formatCurrency(totals.base)}</Text>
                  </Col>

                  <Col span={12}>
                    <Text>IGV {Math.round(igvRate * 100)}%:</Text>
                  </Col>
                  <Col span={12} style={{ textAlign: "right" }}>
                    <Text>{formatCurrency(totals.igv)}</Text>
                  </Col>

                  <Col span={24}>
                    <Divider style={{ margin: "8px 0" }} />
                  </Col>

                  <Col span={12}>
                    <Title level={5} style={{ margin: 0 }}>
                      Total a pagar:
                    </Title>
                  </Col>
                  <Col span={12} style={{ textAlign: "right" }}>
                    <Title level={5} style={{ margin: 0 }}>
                      {formatCurrency(totals.total)}
                    </Title>
                  </Col>
                </Row>
              </Card>
            </Col>
          </Row>
        </Form>
      </Modal>
    </>
  );
};

export default InvoiceCreate;
